import { z } from 'zod'
import { ensureValidAccessToken } from './unified-auth'
import {
  HOMEBODY_ADMIN_X_CONSUMER_HEADER,
  API_TIMEOUT,
  ERROR_MESSAGES,
  HTTP_STATUS,
} from './api.constants'

// Base API error class
export class ApiError extends Error {
  status: number
  path?: string
  timestamp?: string

  constructor(message: string, status: number, path?: string, timestamp?: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.path = path
    this.timestamp = timestamp
  }

  toString(): string {
    return this.message
  }
}

// Create API error from response
export async function createApiErrorFromResponse(response: Response): Promise<ApiError> {
  try {
    const errorData = await response.json()
    return new ApiError(
      errorData.message || response.statusText,
      response.status,
      errorData.path,
      errorData.timestamp
    )
  } catch (e) {
    console.error('Error creating API error from response:', e)
    return new ApiError(response.statusText, response.status)
  }
}

// Enhanced base API call function with authentication and auto-refresh
export async function apiCall(path: string, options: RequestInit = {}): Promise<Response> {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT)

  try {
    if (!import.meta.env.VITE_API_URL) {
      throw new Error('VITE_API_URL is not set')
    }

    // Get valid token for authenticated requests (with auto-refresh)
    const token = await ensureValidAccessToken()

    const response = await fetch(`${import.meta.env.VITE_API_URL}${path}`, {
      ...options,
      signal: controller.signal,
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Consumer': HOMEBODY_ADMIN_X_CONSUMER_HEADER,
        domain: window.location.hostname,
        ...options.headers,
      },
      credentials: 'include', // Important for refresh token cookies
    })

    clearTimeout(timeoutId)

    if (!response.ok) {
      throw await createApiErrorFromResponse(response)
    }

    return response
  } catch (error) {
    clearTimeout(timeoutId)

    if (error instanceof ApiError) {
      throw error
    }

    if (error instanceof Error && error.name === 'AbortError') {
      throw new ApiError(ERROR_MESSAGES.TIMEOUT, HTTP_STATUS.INTERNAL_SERVER_ERROR)
    }

    throw new ApiError(ERROR_MESSAGES.NETWORK_ERROR, HTTP_STATUS.INTERNAL_SERVER_ERROR)
  }
}

// Public API call function (no authentication required)
export async function publicApiCall(path: string, options: RequestInit = {}): Promise<Response> {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT)

  try {
    if (!import.meta.env.VITE_API_URL) {
      throw new Error('VITE_API_URL is not set')
    }

    const response = await fetch(`${import.meta.env.VITE_API_URL}${path}`, {
      ...options,
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        'x-consumer': HOMEBODY_ADMIN_X_CONSUMER_HEADER,
        domain: window.location.hostname,
        ...options.headers,
      },
    })

    clearTimeout(timeoutId)

    if (!response.ok) {
      throw await createApiErrorFromResponse(response)
    }

    return response
  } catch (error) {
    clearTimeout(timeoutId)

    if (error instanceof ApiError) {
      throw error
    }

    if (error instanceof Error && error.name === 'AbortError') {
      throw new ApiError(ERROR_MESSAGES.TIMEOUT, HTTP_STATUS.INTERNAL_SERVER_ERROR)
    }

    throw new ApiError(ERROR_MESSAGES.NETWORK_ERROR, HTTP_STATUS.INTERNAL_SERVER_ERROR)
  }
}

// Generic API function with Zod validation
export async function apiCallWithValidation<T>(
  path: string,
  schema: z.ZodSchema<T>,
  options: RequestInit = {}
): Promise<T> {
  const response = await apiCall(path, options)
  const data = await response.json()
  return schema.parse(data)
}

// Export all API functions
export * from './unified-auth'
export * from './api.constants'
export * from './rewards'
